import React, { useMemo } from 'react';

/**
 * Generative hero background: aurora gradients + an animated "market wave"
 * sine (the site's visual signature) + film grain. Replaces the old raster
 * PNG backgrounds, which showed banding/compression on good screens.
 * Pure CSS/SVG — crisp at any resolution, a few KB, no network requests.
 */
export default function HeroBackdrop({ compact = false }) {
  // One sine period drawn twice side-by-side so translateX(-50%) loops seamlessly.
  const wavePath = useMemo(() => {
    const W = 720; // one period
    const H = 200;
    const mid = H / 2;
    const amp = compact ? 34 : 48;
    const pts = [];
    const N = 96;
    for (let i = 0; i <= N * 2; i++) {
      const x = (i / N) * W;
      const y = mid - Math.sin((i / N) * 2 * Math.PI) * amp;
      pts.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`);
    }
    return pts.join(' ');
  }, [compact]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Aurora blobs */}
      <div
        className="aurora-blob aurora-1"
        style={{ width: '55%', height: '70%', top: '-25%', left: '-10%', background: 'radial-gradient(circle, rgb(var(--accent-rgb) / 0.14), transparent 65%)' }}
      />
      <div
        className="aurora-blob aurora-2"
        style={{ width: '45%', height: '65%', top: '-10%', right: '-8%', background: 'radial-gradient(circle, rgb(var(--accent-rgb) / 0.09), transparent 65%)' }}
      />
      <div
        className="aurora-blob aurora-3"
        style={{ width: '40%', height: '55%', bottom: '-30%', left: '30%', background: 'radial-gradient(circle, rgb(var(--accent-rgb) / 0.06), transparent 65%)' }}
      />

      {/* Animated market waves (two layers, parallax speeds) */}
      <div className="absolute inset-x-0 bottom-0" style={{ height: compact ? '70%' : '60%', opacity: 0.5 }}>
        <svg
          className="market-wave-slow absolute bottom-0 h-full"
          style={{ width: '200%' }}
          viewBox="0 0 1440 200"
          preserveAspectRatio="none"
        >
          <path d={wavePath} fill="none" strokeWidth="1.5" style={{ stroke: 'rgb(var(--accent-rgb) / 0.16)' }} />
        </svg>
        <svg
          className="market-wave absolute bottom-0 h-full"
          style={{ width: '200%', transform: 'translateY(12%)' }}
          viewBox="0 0 1440 200"
          preserveAspectRatio="none"
        >
          <path d={wavePath} fill="none" strokeWidth="1.5" style={{ stroke: 'rgb(var(--accent-rgb) / 0.10)' }} />
        </svg>
      </div>

      {/* Fade the whole backdrop into the page background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-bg" />

      {/* Film grain — kills gradient banding */}
      <div className="noise-overlay" />
    </div>
  );
}
